import React, { useState, useEffect } from 'react';
import { 
  X, 
  Save, 
  Plus, 
  Trash2, 
  AlertCircle,
  CheckCircle2,
  Calendar,
  Type,
  Tag,
  ArrowRightLeft,
  Calculator,
  Lock
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { JournalEntry, JournalEntryLine, AccountingAccount, JournalEntryType } from '../../types';
import { 
  createJournalEntry, 
  updateJournalEntry, 
  groupJournalLines
} from '../../services/accountingService';
import { updateManagedDocument } from '../../services/managementService';
import { useClosingDate } from '../../hooks/useClosingDate';

interface EntryFormModalProps {
  onClose: () => void;
  userId: string;
  accounts: AccountingAccount[];
  editingEntry?: JournalEntry;
  initialData?: Partial<JournalEntry>;
  onSuccess?: (entryId: string) => void;
  linkedDocId?: string;
}

export const EntryFormModal: React.FC<EntryFormModalProps> = ({ 
  onClose, 
  userId, 
  accounts, 
  editingEntry,
  initialData,
  onSuccess,
  linkedDocId
}) => {
  const [date, setDate] = useState((editingEntry?.date || initialData?.date || new Date().toISOString()).split('T')[0]);
  const [description, setDescription] = useState(editingEntry?.description || initialData?.description || '');
  const [type, setType] = useState<JournalEntryType>(editingEntry?.type || initialData?.type || 'OPERATIVO');
  const [documentLocatorCode, setDocumentLocatorCode] = useState(editingEntry?.documentLocatorCode || initialData?.documentLocatorCode || '');
  const [lines, setLines] = useState<JournalEntryLine[]>(
    editingEntry?.lines || initialData?.lines || [
      { accountId: '', debit: 0, credit: 0, description: '' },
      { accountId: '', debit: 0, credit: 0, description: '' }
    ]
  );
  const [loading, setLoading] = useState(false);
  const closingDate = useClosingDate(userId);

  const isLocked = date <= closingDate;
  const isEditLocked = !!(editingEntry && editingEntry.date <= closingDate);

  const selectableAccounts = accounts
    .filter(a => a.isSelectable)
    .sort((a, b) => a.code.localeCompare(b.code));

  const totalDebit = lines.reduce((sum, l) => sum + (parseFloat(l.debit.toString()) || 0), 0);
  const totalCredit = lines.reduce((sum, l) => sum + (parseFloat(l.credit.toString()) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0;
  const difference = totalDebit - totalCredit;

  const handleAddLine = () => {
    if (isEditLocked) return;
    setLines([...lines, { accountId: '', debit: 0, credit: 0, description: '' }]);
  };

  const handleGroupLines = () => {
    if (isEditLocked) return;
    const grouped = groupJournalLines(lines);
    if (grouped.length < lines.length) {
      setLines(grouped);
    }
  };

  const handleRemoveLine = (index: number) => {
    if (isEditLocked) return;
    if (lines.length <= 2) return;
    setLines(lines.filter((_, i) => i !== index));
  };

  const handleLineChange = (index: number, field: keyof JournalEntryLine, value: any) => {
    if (isEditLocked) return;
    const next = [...lines];
    const isNumericField = field === 'debit' || field === 'credit';
    
    // Logic to prevent both debit and credit on same line
    if (field === 'debit' && parseFloat(value) > 0) {
      next[index].credit = 0;
    } else if (field === 'credit' && parseFloat(value) > 0) {
      next[index].debit = 0;
    }

    const finalValue = isNumericField ? (value === '' ? 0 : parseFloat(value)) : value;
    next[index] = { ...next[index], [field]: finalValue };
    setLines(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isBalanced || isLocked) return;

    setLoading(true);
    try {
      const entryData = {
        date,
        description,
        type,
        documentLocatorCode: documentLocatorCode.trim() || null,
        lines: lines.map(l => ({
          accountId: l.accountId,
          description: l.description || '',
          debit: parseFloat(l.debit.toString()) || 0,
          credit: parseFloat(l.credit.toString()) || 0
        })),
      };

      if (editingEntry) {
        await updateJournalEntry(userId, editingEntry.id, entryData);
        if (onSuccess) onSuccess(editingEntry.id);
      } else {
        const res = await createJournalEntry(userId, entryData);
        if (linkedDocId) {
          // Update the management document with the new entry ID
          await updateManagedDocument(userId, linkedDocId, { journalEntryId: res.id });
        }
        if (onSuccess) onSuccess(res.id);
      }
      onClose();
    } catch (error) {
      console.error(error);
      alert("Error al guardar el asiento contable.");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-zinc-900 border border-zinc-800 rounded-3xl w-full max-w-5xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
      >
        <div className="p-8 border-b border-zinc-800 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-white">
              {editingEntry ? 'Editar Asiento' : 'Nuevo Asiento Contable'}
            </h2>
            <p className="text-zinc-500 text-sm">Registro por partida doble equilibrado.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-xl transition-colors">
            <X size={20} className="text-zinc-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-8 overflow-y-auto flex-1 space-y-8">
            {isLocked && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 flex items-start gap-4">
                <Lock className="text-red-400 shrink-0 mt-0.5" size={20} />
                <div>
                  <h4 className="text-red-400 font-bold text-sm">Operación bloqueada</h4>
                  <p className="text-red-400/80 text-xs mt-1">La fecha corresponde a un periodo cerrado.</p>
                </div>
              </div>
            )}

            {/* Header Data */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest pl-1">Fecha</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-3 text-zinc-600" size={16} />
                  <input 
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className={`w-full bg-zinc-800 border ${isLocked ? 'border-red-500/30' : 'border-zinc-700'} rounded-2xl pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors`}
                    required
                    disabled={isEditLocked}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest pl-1">Tipo</label>
                <div className="relative">
                  <Tag className="absolute left-3 top-3 text-zinc-600" size={16} />
                  <select 
                    value={type}
                    onChange={(e) => setType(e.target.value as JournalEntryType)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors appearance-none"
                    disabled={isEditLocked}
                  >
                    <option value="APERTURA">Apertura</option>
                    <option value="OPERATIVO">Operativo</option>
                    <option value="AJUSTE">Ajuste</option>
                    <option value="CIERRE">Cierre</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest pl-1">CÓDIGO DE REFERENCIA</label>
                <div className="relative">
                  <CheckCircle2 className="absolute left-3 top-3 text-emerald-600/50" size={16} />
                  <input 
                    type="text"
                    value={documentLocatorCode}
                    onChange={(e) => setDocumentLocatorCode(e.target.value.toUpperCase().substring(0, 5))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-emerald-500/50 transition-colors font-mono font-bold"
                    placeholder="Ej: TN376"
                    disabled={isEditLocked}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest pl-1">Concepto General</label>
                <div className="relative">
                  <Type className="absolute left-3 top-3 text-zinc-600" size={16} />
                  <input 
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors"
                    placeholder="Descripción del asiento"
                    required
                    disabled={isEditLocked}
                  />
                </div>
              </div>
            </div>

            {/* Lines Table */}
            <div className="space-y-4">
              <div className="flex items-center justify-between px-2">
                <h3 className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Partida Doble</h3>
                <div className="flex items-center gap-4">
                  <button 
                    type="button"
                    onClick={handleGroupLines}
                    disabled={isEditLocked}
                    className={`text-xs flex items-center gap-1.5 font-bold transition-colors ${isEditLocked ? 'text-zinc-700' : 'text-zinc-500 hover:text-zinc-300'}`}
                    title="Agrupa líneas con la misma cuenta contable"
                  >
                    <ArrowRightLeft size={14} />
                    Agrupar Cuentas
                  </button>
                  <button 
                    type="button"
                    onClick={handleAddLine}
                    disabled={isEditLocked}
                    className={`text-xs flex items-center gap-1.5 font-bold transition-colors ${isEditLocked ? 'text-zinc-700' : 'text-indigo-400 hover:text-indigo-300'}`}
                  >
                    <Plus size={14} />
                    Añadir Línea
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <AnimatePresence initial={false}>
                  {lines.map((line, idx) => (
                    <motion.div 
                      key={idx}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      className="grid grid-cols-12 gap-3 items-start group"
                    >
                      <div className="col-span-5 relative">
                        <select 
                          value={line.accountId}
                          onChange={(e) => handleLineChange(idx, 'accountId', e.target.value)}
                          disabled={isEditLocked}
                          className={`w-full bg-zinc-800 border ${line.accountId ? 'border-zinc-700' : 'border-red-500/30'} rounded-2xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-indigo-500 transition-colors appearance-none`}
                          required
                        >
                          <option value="">-- Cuenta Contable --</option>
                          {selectableAccounts.map(acc => (
                            <option key={acc.id} value={acc.id}>{acc.code} - {acc.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-3">
                        <div className="relative">
                          <span className="absolute left-3 top-3 text-[10px] font-bold text-zinc-600">D</span>
                          <input 
                            type="number"
                            step="any"
                            value={line.debit || ''}
                            onChange={(e) => handleLineChange(idx, 'debit', e.target.value)}
                            disabled={isEditLocked}
                            className={`w-full bg-zinc-800 border rounded-2xl pl-8 pr-4 py-2.5 text-xs text-right font-mono focus:outline-none focus:border-emerald-500 transition-colors ${line.debit > 0 ? 'border-emerald-500/30 text-emerald-400' : 'border-zinc-700 text-white'}`}
                            placeholder="Debe"
                          />
                        </div>
                      </div>
                      <div className="col-span-3">
                        <div className="relative">
                          <span className="absolute left-3 top-3 text-[10px] font-bold text-zinc-600">H</span>
                          <input 
                            type="number"
                            step="any"
                            value={line.credit || ''}
                            onChange={(e) => handleLineChange(idx, 'credit', e.target.value)}
                            disabled={isEditLocked}
                            className={`w-full bg-zinc-800 border rounded-2xl pl-8 pr-4 py-2.5 text-xs text-right font-mono focus:outline-none focus:border-red-500 transition-colors ${line.credit > 0 ? 'border-red-500/30 text-red-400' : 'border-zinc-700 text-white'}`}
                            placeholder="Haber"
                          />
                        </div>
                      </div>
                      <div className="col-span-1 flex items-center pt-2.5 justify-center">
                        <button 
                          type="button"
                          onClick={() => handleRemoveLine(idx)}
                          disabled={lines.length <= 2 || isEditLocked}
                          className="text-zinc-600 hover:text-red-400 disabled:opacity-0 transition-all p-1"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Footer Bar with Balance Check */}
          <div className="p-8 bg-zinc-950/50 border-t border-zinc-800 shrink-0">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-center gap-8 font-mono text-sm">
                <div className="text-right">
                  <div className="text-[10px] text-zinc-600 uppercase font-bold tracking-widest mb-1">Total Debe</div>
                  <div className="text-emerald-500 font-bold">{formatCurrency(totalDebit)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-zinc-600 uppercase font-bold tracking-widest mb-1">Total Haber</div>
                  <div className="text-red-500 font-bold">{formatCurrency(totalCredit)}</div>
                </div>
                <div className="border-l border-zinc-800 h-8 hidden md:block" />
                <div className="flex items-center gap-3">
                  {isBalanced ? (
                    <div className="flex items-center gap-2 text-emerald-500 bg-emerald-500/10 px-4 py-2 rounded-xl border border-emerald-500/20">
                      <CheckCircle2 size={16} />
                      <span className="text-[11px] font-bold uppercase tracking-wider">Asiento Balanceado</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-red-500 bg-red-500/10 px-4 py-2 rounded-xl border border-red-500/20">
                      <AlertCircle size={16} />
                      <span className="text-[11px] font-bold uppercase tracking-wider">
                        Descuadre: {formatCurrency(Math.abs(difference))}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 min-w-[300px]">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-2xl font-bold transition-all"
                >
                  {isLocked ? 'Cerrar' : 'Cancelar'}
                </button>
                {!isLocked && (
                  <button
                    type="submit"
                    disabled={loading || !isBalanced}
                    className="flex-1 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-bold transition-all shadow-lg shadow-indigo-500/20 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save size={18} />
                    {loading ? 'Guardando...' : editingEntry ? 'Actualizar' : 'Confirmar'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </motion.div>
    </div>
  );
};
